import {
  ModalV2,
  FireflyButton,
  ChakraFlex,
  FAIconName,
  useToastMessage,
  ToastMessage,
  ChakraBox,
} from '@equip.health/ui';
import UploadDocumentsList from './UploadDocumentList';
import { useEffect, useState } from 'react';
import { getFileKey } from '~/lib/util/patient.util';
import {
  INVALID_FILE_NAME_MESSAGE,
  UPLOAD_DOCUMENT,
} from '~/lib/constants/documentCenter.constants';
import {
  documentsApi,
  useDeletePatientDocumentMutation,
  useUploadPatientDocumentMutation,
} from '~/store/services/documents';
import fileUtil, { getFileExtension } from '~/lib/util/file.util';
import useS3MultiFileUpload from '~/lib/hooks/useS3MultiFileUpload';
import { CacheTag } from '~/store/services/CacheTag';
import { useDispatch } from 'react-redux';
import { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';

interface UploadConfirmationModalProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  uploadedFiles?: File[];
  uploadErrorMessages?: string[];
  onUploadSuccess?: (uploaded: AttachmentType[]) => void;
}

const UploadConfirmationModal = ({
  id,
  isOpen,
  onClose,
  onUploadSuccess,
  patientId,
  uploadedFiles = [],
  uploadErrorMessages = [],
}: UploadConfirmationModalProps) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const [files, setFiles] = useState<File[]>(uploadedFiles);
  const [fileMetadata, setFileMetadata] = useState<
    Record<string, FileMetadata>
  >({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [visibleErrors, setVisibleErrors] =
    useState<string[]>(uploadErrorMessages);

  const [uploadPatientDocument, { isLoading }] =
    useUploadPatientDocumentMutation();
  const [deletePatientDocument] = useDeletePatientDocumentMutation();
  const { createToast } = useToastMessage();

  const { uploadMultiple, loading: isS3Uploading } = useS3MultiFileUpload();

  useEffect(() => {
    setVisibleErrors((prev) => {
      const same = JSON.stringify(prev) === JSON.stringify(uploadErrorMessages);
      return same ? prev : uploadErrorMessages;
    });

    if (uploadErrorMessages.length === 0) return undefined;

    const timer = setTimeout(() => {
      setVisibleErrors([]);
    }, 8000);

    return () => clearTimeout(timer);
  }, [uploadErrorMessages]);

  useEffect(() => {
    const initialMetadata: Record<string, FileMetadata> = {};

    uploadedFiles.forEach((file) => {
      const key = getFileKey(file);
      initialMetadata[key] = {
        channelId: null,
        fileName: fileUtil.getFileNameWithoutExtension(file.name),
        fileSize: file.size,
        fileSource: '',
        patientExternalId: patientId,
        sensitive: false,
        tags: [],
        type: 'PATIENT',
      };
    });

    setFileMetadata(initialMetadata);
    setFiles(uploadedFiles);
  }, [uploadedFiles, patientId]);

  const updateMetadata = (file: File, data: Partial<FileMetadata>) => {
    const key = getFileKey(file);

    setFileMetadata((prev) => {
      const updated = {
        ...prev,
        [key]: {
          ...prev[key],
          ...data,
        },
      };
      const hasInvalidName = files.some((f) => {
        const meta = updated[getFileKey(f)];
        return !fileUtil.validateFileName(meta?.fileName);
      });

      if (!hasInvalidName) {
        setErrorMessage(null);
      }

      return updated;
    });
  };

  const handleRemoveFile = (fileToRemove: File) => {
    const key = getFileKey(fileToRemove);
    setFiles((prev) => prev.filter((f) => getFileKey(f) !== key));
    setFileMetadata((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });

    if (files.length === 1) {
      onClose();
    }
  };

  const onToggleSensitive = (file: File) => {
    const key = getFileKey(file);
    setFileMetadata((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        sensitive: !(prev[key]?.sensitive ?? false),
      },
    }));
  };

  const hasInvalidFileNames = (files: File[], metadata: Record<string, any>) =>
    files.some((file) => {
      const meta = metadata[getFileKey(file)];
      return !fileUtil.validateFileName(meta?.fileName);
    });

  const presignFile = async (
    file: File,
    metadata: Record<string, FileMetadata>,
  ): Promise<PresignResult> => {
    const key = getFileKey(file);
    const meta = metadata[key];
    if (!meta) return { file, success: false };

    const ext = getFileExtension(file.name.toLowerCase());
    if (!ext) return { file, success: false };

    const cleanName =
      meta.fileName ?? fileUtil.getFileNameWithoutExtension(file.name);

    try {
      const { data } = await uploadPatientDocument({
        ...meta,
        fileName: `${cleanName}.${ext}`,
        fileSize: file.size,
      }).unwrap();

      if (!data?.uploadUrl) throw new Error('Missing upload URL');

      return {
        documentExternalId: data.documentKey,
        file,
        success: true,
        url: data.uploadUrl,
      };
    } catch (err) {
      console.error('Presign failed:', file.name, err);
      return { file, success: false };
    }
  };

  const voidFailedDocument = async (
    file: File,
    presignMap: Map<File, string>,
  ) => {
    const documentExternalId = presignMap.get(file);
    if (!documentExternalId) return;

    try {
      await deletePatientDocument({
        documentExternalId,
        patientExternalId: patientId,
        voidReasonCode: 'OTHER',
      }).unwrap();
    } catch (err) {
      console.error(
        'Cleanup failed after S3 upload error:',
        documentExternalId,
        err,
      );
    }
  };
  const handleClose = () => {
    trackEvent(DocumentCenterEvents.uploadModalCloseIconClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
    });
    onClose();
  };

  const handleUpload = async () => {
    trackEvent(DocumentCenterEvents.uploadModalUploadButtonClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
    });
    setErrorMessage(null);
    const filesToUpload = [...files];
    const metadataSnapshot = { ...fileMetadata };
    if (hasInvalidFileNames(filesToUpload, metadataSnapshot)) {
      setErrorMessage(INVALID_FILE_NAME_MESSAGE);
      return;
    }
    const failedFiles: File[] = [];
    const presignResults = await Promise.all(
      filesToUpload.map((file) => presignFile(file, metadataSnapshot)),
    );
    const successfulRequests = presignResults.filter(
      (result): result is PresignSuccess =>
        result.success && !!result.url && !!result.documentExternalId,
    );

    const presignMap = new Map(
      successfulRequests.map((result) => [
        result.file,
        result.documentExternalId,
      ]),
    );

    failedFiles.push(
      ...presignResults
        .filter((result) => !result.success)
        .map((result) => result.file),
    );

    if (successfulRequests.length > 0) {
      const s3Results = await uploadMultiple(successfulRequests);

      const failedUploads = s3Results.filter((r) => !r.success);
      failedFiles.push(...failedUploads.map((r) => r.file));
      await Promise.allSettled(
        failedUploads.map((r) => voidFailedDocument(r.file, presignMap)),
      );

      const successfulUploads = s3Results.filter((r) => r.success);
      if (successfulUploads.length > 0) {
        const uploadedDocuments = successfulUploads.map((r) => {
          const key = getFileKey(r.file);
          const meta = metadataSnapshot[key];
          const ext = getFileExtension(r.file.name);

          return {
            attachmentExternalId: presignMap.get(r.file),
            fileName: ext
              ? `${meta?.fileName ?? r.file.name}.${ext}`
              : (meta?.fileName ?? r.file.name),
            statusCode: null,
          };
        });

        onUploadSuccess?.(uploadedDocuments);
      }
    }

    if (failedFiles.length > 0) {
      const message = `${failedFiles
        .map((f) => f.name)
        .join(', ')} may be corrupted and cannot be uploaded.`;
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: 'file-upload-failed-toast',
        text: message,
        variant: 'critical',
      });
      onClose();
      return;
    }
    dispatch(documentsApi.util.invalidateTags([CacheTag.PatientDocuments]));
    setTimeout(() => {
      dispatch(documentsApi.util.invalidateTags([CacheTag.PatientDocuments]));
    }, 10000);
    createToast({
      icon: FAIconName.checkRegular,
      id: 'file-upload-success-toast',
      text: `${
        filesToUpload.length > 1 ? 'Documents' : 'Document'
      } uploaded successfully.`,
      variant: 'success',
    });
    onClose();
  };
  const FooterContent = () => (
    <ChakraFlex justifyContent="space-between" width="100%">
      <FireflyButton
        id="cancel"
        leftIcon={FAIconName.timesRegular}
        onClick={() => {
          trackEvent(DocumentCenterEvents.uploadModalCancelClicked, {
            Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          });
          onClose();
        }}
        variant="secondary"
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id="upload-documents"
        isDisabled={isLoading || isS3Uploading}
        isLoading={isLoading || isS3Uploading}
        leftIcon={FAIconName.checkRegular}
        onClick={handleUpload}
        variant="primary"
      >
        {UPLOAD_DOCUMENT}
      </FireflyButton>
    </ChakraFlex>
  );

  return (
    <ModalV2
      footerContent={<FooterContent />}
      id={`${id}__upload-confirmation-modal`}
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      title={`Upload ${files.length > 1 ? 'documents' : 'document'} to patient chart`}
    >
      <UploadDocumentsList
        files={files}
        metadata={fileMetadata}
        modalId={`${id}__upload-confirmation-modal`}
        onMetadataChange={updateMetadata}
        onRemoveFile={handleRemoveFile}
        onToggleSensitive={onToggleSensitive}
      />
      {visibleErrors.length > 0 && (
        <ChakraFlex flexDirection="column" gap="12px" marginTop="24px">
          {visibleErrors.map((msg, index) => (
            <ToastMessage
              icon={FAIconName.exclamationTriangleRegular}
              id={`pre-upload-error-${index}`}
              isFullWidth
              key={msg}
              text={msg}
              variant="critical"
            />
          ))}
        </ChakraFlex>
      )}

      {errorMessage && (
        <ChakraBox marginTop="24px">
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id="runtime-upload-error"
            isFullWidth
            text={errorMessage}
            variant="critical"
          />
        </ChakraBox>
      )}
    </ModalV2>
  );
};

export default UploadConfirmationModal;
